import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2 } from "lucide-react";
import api from "@/lib/api";

export default function ChangePassword() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  if (!user) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("Password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      await refresh();
      toast.success("Password updated");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 bg-slate-50">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
        <div className="h-12 w-12 rounded-xl bg-indigo-50 grid place-items-center text-indigo-600 mb-4">
          <KeyRound className="w-6 h-6" />
        </div>
        <h1 className="font-display font-bold text-2xl text-slate-900">
          {user.must_change_password ? "Set a new password" : "Change password"}
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          {user.must_change_password
            ? "Your account uses a temporary password. Please choose a new one to continue."
            : "Choose a strong password for your account."}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label>Current password</Label>
            <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)}
                   data-testid="cp-current" className="mt-1.5 h-11" />
          </div>
          <div>
            <Label>New password</Label>
            <Input type="password" required value={next} onChange={(e) => setNext(e.target.value)}
                   data-testid="cp-new" className="mt-1.5 h-11" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   data-testid="cp-confirm" className="mt-1.5 h-11" />
          </div>
          <Button type="submit" disabled={loading} data-testid="cp-submit"
                  className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
          </Button>
          {!user.must_change_password && (
            <button type="button" onClick={() => nav(-1)} className="w-full text-sm text-slate-500 hover:text-slate-700">
              Cancel
            </button>
          )}
          {user.must_change_password && (
            <button type="button" onClick={logout} className="w-full text-sm text-slate-500 hover:text-slate-700">
              Sign out
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
